import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
    endpoints,
    type HideReviewPhotoBody,
    type ModerateReviewBody,
    type SaveStorefrontSiteBody,
    type StorefrontImageUse,
    type StorefrontMaintenanceBody,
    type StorefrontSite,
    type StorefrontThemeBody,
} from './api';
import { usePermittedMutation } from './session';

/** The seller's own shop. */

export function useStorefrontSites(enabled = true) {
    return useQuery({
        queryKey: ['storefront-sites'],
        queryFn: endpoints.storefrontSites,
        enabled,
    });
}

/**
 * A picture for the shop — a banner, its logo or its icon — uploaded before the
 * settings that point at it are saved. Its use sets the smallest the API takes.
 */
export function useUploadStorefrontImage() {
    return usePermittedMutation('storefront.write', {
        mutationFn: ({ file, use }: { file: File; use: StorefrontImageUse }) => endpoints.uploadStorefrontImage(file, use),
    });
}

/**
 * Where a shop is reachable. The seller's own domain wins once it is proven —
 * pass it only then (`provenDomain`). Otherwise the shop lives on its slug as
 * a subdomain — <slug>.localhost:<port> in development (the storefront's own
 * dev port, VITE_SHOP_PORT, default 5175), <slug>.<apex> in production
 * (panel.example.com -> <slug>.example.com). The shop resolves itself from
 * that host, so no ?shop=.
 */
export function shopAddress(slug: string, domain?: string): string {
    if (domain) return `https://${domain}`;
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host.endsWith('.localhost');
    const shopPort = import.meta.env.VITE_SHOP_PORT ?? '5175';
    return isLocal ? `${window.location.protocol}//${slug}.localhost:${shopPort}` : `https://${slug}.${host.replace(/^panel\./, '')}`;
}

/**
 * The shop's own domain where the seller has proven it theirs, and nothing
 * otherwise: a domain only saved is a claim, which the API uses nowhere and
 * the shop does not answer on (EPZ-0160).
 */
export function provenDomain(site: Pick<StorefrontSite, 'domain' | 'domainVerifiedAt'> | undefined): string | undefined {
    return site?.domainVerifiedAt ? site.domain : undefined;
}

/** Look up the shop's domain record, which proves the domain the seller's. */
export function useVerifyStorefrontDomain() {
    const queryClient = useQueryClient();
    return usePermittedMutation('storefront.write', {
        mutationFn: () => endpoints.verifyStorefrontDomain(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['storefront-sites'] });
            // A proven domain becomes the address the shop's channel is named
            // by, which the channels screen shows.
            queryClient.invalidateQueries({ queryKey: ['channel-accounts'] });
        },
    });
}

export function useSaveStorefrontSite() {
    const queryClient = useQueryClient();
    return usePermittedMutation('storefront.write', {
        mutationFn: (body: SaveStorefrontSiteBody) => endpoints.saveStorefrontSite(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['storefront-sites'] });
            // Saving the shop provisions or updates its channel, so the channels
            // screen has to refetch to show it.
            queryClient.invalidateQueries({ queryKey: ['channel-accounts'] });
        },
    });
}

/**
 * The shop's look (docs/14 §9.3): what renders now, the draft Görünüm edits,
 * and the last ten published looks to go back to.
 */
export function useStorefrontTheme(enabled = true) {
    return useQuery({
        queryKey: ['storefront-theme'],
        queryFn: endpoints.storefrontTheme,
        enabled,
    });
}

/** Keep a look as the draft. Nothing a shopper sees changes. */
export function useSaveStorefrontThemeDraft() {
    const queryClient = useQueryClient();
    return usePermittedMutation('storefront.write', {
        mutationFn: (body: StorefrontThemeBody) => endpoints.saveStorefrontThemeDraft(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['storefront-theme'] }),
    });
}

/** Throw the draft away; Görünüm starts again from the live look. */
export function useDiscardStorefrontThemeDraft() {
    const queryClient = useQueryClient();
    return usePermittedMutation('storefront.write', {
        mutationFn: () => endpoints.discardStorefrontThemeDraft(),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['storefront-theme'] }),
    });
}

/** Make a look the one the shop renders. */
export function usePublishStorefrontTheme() {
    const queryClient = useQueryClient();
    return usePermittedMutation('storefront.write', {
        mutationFn: (body: StorefrontThemeBody) => endpoints.publishStorefrontTheme(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['storefront-theme'] });
            // The site carries the live accent and logo the other tabs read.
            queryClient.invalidateQueries({ queryKey: ['storefront-sites'] });
        },
    });
}

/** Publish an earlier look again: the rollback. */
export function useRestoreStorefrontTheme() {
    const queryClient = useQueryClient();
    return usePermittedMutation('storefront.write', {
        mutationFn: (versionId: string) => endpoints.restoreStorefrontTheme(versionId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['storefront-theme'] });
            queryClient.invalidateQueries({ queryKey: ['storefront-sites'] });
        },
    });
}

/** Delete the shop. Soft: the row is kept but the shop disappears from every
 *  screen and stops answering, and its slug is free to take again. */
export function useDeleteStorefrontSite() {
    const queryClient = useQueryClient();
    return usePermittedMutation('storefront.write', {
        mutationFn: () => endpoints.deleteStorefrontSite(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['storefront-sites'] });
            queryClient.invalidateQueries({ queryKey: ['channel-accounts'] });
        },
    });
}

/** Open or close the shop — its active/passive state, shared with the shop
 *  settings so both screens flip the same flag. */
export function useSetShopPublished() {
    const queryClient = useQueryClient();
    return usePermittedMutation('storefront.write', {
        mutationFn: (published: boolean) => endpoints.setStorefrontPublished(published),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['storefront-sites'] });
            queryClient.invalidateQueries({ queryKey: ['channel-accounts'] });
        },
    });
}

/**
 * Put the shop in maintenance with its message, or take it out (docs/14 §13).
 * A shop in maintenance answers its maintenance page with a 503 and sells
 * nothing; closing it is the other switch, and answers as no shop at all.
 */
export function useSetShopMaintenance() {
    const queryClient = useQueryClient();
    return usePermittedMutation('storefront.write', {
        mutationFn: (body: StorefrontMaintenanceBody) => endpoints.setStorefrontMaintenance(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['storefront-sites'] }),
    });
}

/** A one-minute link that opens the shop in maintenance for the seller alone. */
export function useShopPreview() {
    return usePermittedMutation('storefront.read', {
        mutationFn: () => endpoints.issueStorefrontPreview(),
    });
}

/** Reviews written on the shop, pending first. */
export function useStorefrontReviews(status: string) {
    return useQuery({
        queryKey: ['storefront-reviews', status],
        queryFn: () => endpoints.storefrontReviews(status),
    });
}

/** Hide a buyer's photograph from the shop, or show it again; the review stays as it is. */
export function useHideReviewPhoto() {
    const queryClient = useQueryClient();
    return usePermittedMutation('storefront.write', {
        mutationFn: ({ reviewId, photoId, body }: { reviewId: string; photoId: string; body: HideReviewPhotoBody }) =>
            endpoints.hideStorefrontReviewPhoto(reviewId, photoId, body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['storefront-reviews'] }),
    });
}

export function useModerateReview() {
    const queryClient = useQueryClient();
    return usePermittedMutation('storefront.write', {
        mutationFn: ({ id, body }: { id: string; body: ModerateReviewBody }) => endpoints.moderateStorefrontReview(id, body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['storefront-reviews'] }),
    });
}
